// Transactions (Stripe entitlements, newest first) and Webhook events (the newest Stripe events and how the
// server handled them) for one mode, contracts/web.md §5.7. Paged with "Load more"; players link to their detail;
// Stripe links open the dashboard in a new tab.

import { Link } from "react-router";
import { useTransactions, useWebhookEvents } from "../../api/payments";
import { tableStyles } from "../../components/table";
import { Button, Card, EmptyState, ErrorState, Loading } from "../../components/ui";
import { formatDateTime } from "../../lib/dates";
import { shortId } from "../../lib/format";
import { formatMoney, stripeDashboardUrl, WEB_PLAN_LABEL, WEB_PLANS, type PaymentMode, type WebPlan } from "../../lib/payments";
import pages from "../pages.module.css";
import { EventResultPill, StripeLink, txStatus, TxStatusPill } from "./components";
import p from "./payments.module.css";

function PlayerLink({ userId, handle }: { userId: string | null; handle: string | null }) {
  if (!userId) return <span className={pages.faint}>—</span>;
  return (
    <Link to={`/players/${userId}`} className={tableStyles.id} title={userId}>
      {handle ? `@${handle}` : shortId(userId)}
    </Link>
  );
}

const planLabel = (plan: string | null) => (plan && (WEB_PLANS as readonly string[]).includes(plan) ? WEB_PLAN_LABEL[plan as WebPlan] : (plan ?? "—"));

export function TransactionsTable({ mode }: { mode: PaymentMode }) {
  const q = useTransactions(mode);
  const title = `Transactions · ${mode} mode`;
  if (q.isPending)
    return (
      <Card title={title}>
        <Loading rows={6} />
      </Card>
    );
  if (q.isError)
    return (
      <Card title={title}>
        <ErrorState error={q.error} onRetry={() => void q.refetch()} />
      </Card>
    );
  const items = q.data.pages.flatMap((page) => page.items);
  return (
    <Card title={title} actions={<span className="monoLabel">{items.length} shown</span>}>
      {items.length === 0 ? (
        <EmptyState title={`No ${mode} transactions yet`}>Purchases show here once players check out on the web.</EmptyState>
      ) : (
        <div className={tableStyles.wrap}>
          <table className={tableStyles.table} data-testid="transactions">
            <thead>
              <tr>
                <th>Date</th>
                <th>Player</th>
                <th>Plan</th>
                <th data-align="right">Amount</th>
                <th>Status</th>
                <th>Ends</th>
                <th>Stripe</th>
              </tr>
            </thead>
            <tbody>
              {items.map((t) => {
                const status = txStatus(t);
                return (
                  <tr key={t.id} data-testid="transaction-row" data-status={status}>
                    <td className="mono">{formatDateTime(t.createdAt)}</td>
                    <td>
                      <PlayerLink userId={t.userId} handle={t.handle} />
                    </td>
                    <td>
                      {planLabel(t.plan)}
                      {t.packageName && t.packageName !== planLabel(t.plan) ? <span className={pages.faint}> · {t.packageName}</span> : null}
                    </td>
                    <td data-align="right" className="mono">
                      {formatMoney(t.amount, t.currency)}
                    </td>
                    <td>
                      <TxStatusPill status={status} />
                    </td>
                    <td className="mono">
                      {t.revokedAt
                        ? `revoked ${formatDateTime(t.revokedAt)}`
                        : t.expiresAt === null
                          ? "never"
                          : `${Date.parse(t.expiresAt) > Date.now() ? (t.willRenew ? "renews" : "ends") : "ended"} ${formatDateTime(t.expiresAt)}`}
                    </td>
                    <td>
                      <StripeLink href={t.stripeUrl} />
                    </td>
                  </tr>
                );
              })}
            </tbody>
          </table>
        </div>
      )}
      {q.hasNextPage ? (
        <div className={pages.loadMore}>
          <Button size="sm" onClick={() => void q.fetchNextPage()} busy={q.isFetchingNextPage} data-testid="transactions-more">
            Load more
          </Button>
        </div>
      ) : null}
    </Card>
  );
}

export function WebhookEventsTable({ mode }: { mode: PaymentMode }) {
  const q = useWebhookEvents(mode);
  const title = `Webhook events · ${mode} mode`;
  if (q.isPending)
    return (
      <Card title={title}>
        <Loading rows={6} />
      </Card>
    );
  if (q.isError)
    return (
      <Card title={title}>
        <ErrorState error={q.error} onRetry={() => void q.refetch()} />
      </Card>
    );
  const items = q.data.pages.flatMap((page) => page.items);
  return (
    <Card title={title} actions={<span className="monoLabel">{items.length} shown</span>}>
      {items.length === 0 ? (
        <EmptyState title={`No ${mode} events yet`}>Stripe sends events here once checkout runs in this mode.</EmptyState>
      ) : (
        <div className={tableStyles.wrap}>
          <table className={tableStyles.table} data-testid="webhook-events">
            <thead>
              <tr>
                <th>Received</th>
                <th>Type</th>
                <th>Result</th>
                <th>Player</th>
                <th>Object</th>
                <th>Stripe</th>
              </tr>
            </thead>
            <tbody>
              {items.map((e) => (
                <tr key={e.id} data-testid="event-row" data-result={e.result ?? "pending"}>
                  <td className="mono">{formatDateTime(e.createdAt)}</td>
                  <td className="mono">{e.type}</td>
                  <td>
                    <EventResultPill result={e.result} />
                    {e.error ? (
                      <span className={p.eventError}>{e.error}</span>
                    ) : null}
                  </td>
                  <td>
                    <PlayerLink userId={e.userId} handle={e.handle} />
                  </td>
                  <td className={`mono ${pages.faint}`}>{e.objectId ? shortId(e.objectId, 18) : "—"}</td>
                  <td>
                    <StripeLink href={e.stripeUrl ?? stripeDashboardUrl(e.mode ?? mode, `events/${e.id}`)} />
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}
      {q.hasNextPage ? (
        <div className={pages.loadMore}>
          <Button size="sm" onClick={() => void q.fetchNextPage()} busy={q.isFetchingNextPage} data-testid="events-more">
            Load more
          </Button>
        </div>
      ) : null}
    </Card>
  );
}
